import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';
import { quickFacts } from '../../mocks/transportationPage';
import { transportationTips } from '../../mocks/transportation';
import TransitSection from './components/TransitSection';
import RideshareSection from './components/RideshareSection';
import BikingSection from './components/BikingSection';
import ParkingSection from './components/ParkingSection';
import AirportSection from './components/AirportSection';
import SafetyTipsSection from './components/SafetyTipsSection';
import MailchimpNewsletter from '../../components/feature/MailchimpNewsletter';

export default function TransportationPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeNav, setActiveNav] = useState('');

  useSEO({
    title: 'Roseville Transportation Getting Around Guide | Transit Parking Biking',
    description:
      'Complete transportation guide for Roseville California. Public transit routes, parking locations, bike trails, rideshare options, airport connections, and commuter services. Navigate Roseville with ease.',
    keywords:
      'Roseville transportation, Roseville transit, Roseville parking, Roseville bike trails, getting around Roseville, Sacramento airport shuttle',
    canonical: '/transportation',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(
          'Transportation & Getting Around - Roseville, California',
          'Complete guide to transportation options in Roseville including public transit, parking, biking, rideshare, and airport connections',
          '/transportation'
        ),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Transportation', url: '/transportation' }
        ]),
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'How do I get around Roseville without a car?',
              acceptedAnswer: {
                '@type': 'Answer',
                text:
                  'Roseville Transit operates 6 bus routes connecting major destinations. Rideshare services like Uber and Lyft are also available 24/7. The city has 45+ miles of bike trails for cycling.'
              }
            },
            {
              '@type': 'Question',
              name: 'Is parking free in Roseville?',
              acceptedAnswer: {
                '@type': 'Answer',
                text:
                  'Most parking in Roseville is free, including the Westfield Galleria, Civic Center, and park lots. The Downtown Parking Garage offers free 2-hour parking.'
              }
            },
            {
              '@type': 'Question',
              name: 'How far is Sacramento Airport from Roseville?',
              acceptedAnswer: {
                '@type': 'Answer',
                text:
                  'Sacramento International Airport (SMF) is approximately 18 miles from Roseville, about a 25-35 minute drive. Rideshare, shuttle, and public transit options are available.'
              }
            }
          ]
        }
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      const sections = ['transit', 'rideshare', 'biking', 'parking', 'airport'];
      for (const section of sections) {
        const el = document.getElementById(section);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom > 120) {
            setActiveNav(section);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'transit', label: 'Transit', icon: 'ri-bus-2-line' },
    { id: 'rideshare', label: 'Rideshare', icon: 'ri-taxi-line' },
    { id: 'biking', label: 'Biking', icon: 'ri-bike-line' },
    { id: 'parking', label: 'Parking', icon: 'ri-parking-box-line' },
    { id: 'airport', label: 'Airport', icon: 'ri-flight-takeoff-line' }
  ];

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 cursor-pointer">
              <div className={`text-2xl font-bold transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                <span className="font-black">ROSEVILLE</span>
                <span className="text-teal-500">.org</span>
              </div>
            </Link>

            <div className="hidden lg:flex items-center space-x-8">
              <Link
                to="/"
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
                }`}
              >
                Home
              </Link>
              <Link
                to="/events"
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
                }`}
              >
                Events
              </Link>
              <Link
                to="/food-drink"
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
                }`}
              >
                Food &amp; Drink
              </Link>
              <Link
                to="/recreation"
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
                }`}
              >
                Recreation
              </Link>
              <Link
                to="/transportation"
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-teal-600 font-semibold' : 'text-teal-300'
                }`}
              >
                Transportation
              </Link>
              <Link
                to="/directory"
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
                }`}
              >
                Directory
              </Link>
            </div>

            <Link
              to="/deals"
              className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              View Deals
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[520px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/ac08b43b55c5786bb7c206a6ad1b56c1.jpg"
            alt="Roseville Transportation"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/55"></div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto w-full">
          <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <i className="ri-compass-3-line text-teal-300"></i>
            <span className="text-white text-sm font-medium">Your Complete Guide</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Transportation &amp;<br />
            <span className="text-teal-400">Getting Around</span>
          </h1>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Navigate Roseville with ease — public transit, bike trails, rideshare, parking, and airport connections all in one
            place.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="#transit"
              onClick={e => {
                e.preventDefault();
                scrollToSection('transit');
              }}
              className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-3 rounded-full font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-bus-2-line mr-2"></i>Transit Routes
            </a>
            <a
              href="#parking"
              onClick={e => {
                e.preventDefault();
                scrollToSection('parking');
              }}
              className="bg-white/20 hover:bg-white/30 text-white px-8 py-3 rounded-full font-semibold transition-colors backdrop-blur-sm whitespace-nowrap cursor-pointer"
            >
              <i className="ri-parking-box-line mr-2"></i>Find Parking
            </a>
          </div>
        </div>
      </section>

      {/* Quick Facts Bar */}
      <section className="bg-teal-600 py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {quickFacts.map((fact, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className={`${fact.icon} text-2xl text-white`}></i>
                </div>
                <p className="text-3xl font-bold text-white">{fact.value}</p>
                <p className="text-teal-100 text-sm">{fact.label}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-6 border-t border-white/20 text-center">
            <p className="text-sm text-teal-100">
              Data provided by City of Roseville Public Works Department & Roseville Transit
            </p>
            <p className="text-xs text-teal-200/50 mt-1">
              <i className="ri-time-line mr-1"></i>Data last updated: January 20, 2025
            </p>
          </div>
        </div>
      </section>

      {/* Sticky Section Nav */}
      <div className="sticky top-[72px] z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-center space-x-1 overflow-x-auto py-3">
            {navItems.map(item => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`flex items-center space-x-2 px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeNav === item.id
                    ? 'bg-teal-600 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <i className={`${item.icon} text-base`}></i>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Tips */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {transportationTips.map((tip, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 text-center hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-full mx-auto mb-3">
                  <i className={`${tip.icon} text-2xl text-teal-600`}></i>
                </div>
                <h4 className="font-semibold text-gray-900 text-sm mb-1">{tip.title}</h4>
                <p className="text-gray-500 text-xs">{tip.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Sections */}
      <TransitSection />
      <RideshareSection />
      <BikingSection />
      <ParkingSection />
      <AirportSection />
      <SafetyTipsSection />

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-600 text-sm">Common questions about getting around Roseville</p>
          </div>

          <div className="space-y-4">
            <FAQItem
              question="How do I get around Roseville without a car?"
              answer="Roseville Transit operates 6 bus routes connecting major destinations including downtown, Westfield Galleria, and Sacramento. Rideshare services like Uber and Lyft are available 24/7, and the city has over 45 miles of bike trails for cycling."
            />
            <FAQItem
              question="Is parking free in Roseville?"
              answer="Yes! Most parking in Roseville is free, including the Westfield Galleria (6,000 spaces), Civic Center, park lots, and the Transit Center Park &amp; Ride. The Downtown Parking Garage offers free 2-hour parking with $1/hr after."
            />
            <FAQItem
              question="How far is Sacramento Airport from Roseville?"
              answer="Sacramento International Airport (SMF) is approximately 18 miles from Roseville, about a 25-35 minute drive depending on traffic. You can get there via rideshare, airport shuttle, or the Roseville Commuter bus to Sacramento."
            />
            <FAQItem
              question="Can I bring my bike on the bus?"
              answer="Absolutely! All Roseville Transit buses are equipped with front-mounted bike racks that hold 2-3 bikes. Simply load your bike before boarding — no extra fare required."
            />
            <FAQItem
              question="What are the transit fares?"
              answer="A single ride costs $1.50 (cash or Clipper card). Day passes are $4.00 for unlimited rides. Monthly passes are $50.00. Seniors and disabled riders pay $0.75, youth (5-18) pay $1.00, and children under 5 ride free with a paying adult."
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Explore Roseville?</h2>
          <p className="text-teal-100 mb-8 max-w-2xl mx-auto">
            Download the Roseville Transit app for real-time bus tracking, trip planning, and mobile fare payment. Getting
            around has never been easier.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://www.roseville.ca.us/government/departments/public_works/roseville_transit"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-teal-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-download-line mr-2"></i>Get Transit App
            </a>
            <a
              href="tel:9167457560"
              className="bg-teal-700 hover:bg-teal-800 text-white px-8 py-3 rounded-full font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>Call (916) 745-7560
            </a>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <MailchimpNewsletter />

      {/* Footer */}
      <footer className="bg-gradient-to-br from-teal-800 via-teal-900 to-teal-800 text-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <Link to="/" className="text-2xl font-bold mb-4 block cursor-pointer">
                <span className="font-black">ROSEVILLE</span>
                <span className="text-amber-400">.org</span>
              </Link>
              <p className="text-white/80 text-sm leading-relaxed mb-4">
                Your official guide to Roseville, California. Discover attractions, events, dining, and everything our
                vibrant city has to offer.
              </p>
              <p className="text-white/80 text-sm">
                5098 Foothills Blvd, Suite 3-402<br />
                Roseville, California 95747
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Explore</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/events" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">
                    Events
                  </Link>
                </li>
                <li>
                  <Link
                    to="/food-drink"
                    className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer"
                  >
                    Food &amp; Drink
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">
                    Blog &amp; News
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Services</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/recreation" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">
                    Recreation
                  </Link>
                </li>
                <li>
                  <Link
                    to="/transportation"
                    className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer"
                  >
                    Transportation
                  </Link>
                </li>
                <li>
                  <Link to="/utilities" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">
                    Utilities
                  </Link>
                </li>
                <li>
                  <Link to="/jobs" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">
                    Jobs
                  </Link>
                </li>
                <li>
                  <Link
                    to="/deals"
                    className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer"
                  >
                    Deals &amp; Coupons
                  </Link>
                </li>
                <li>
                  <Link
                    to="/directory"
                    className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer"
                  >
                    Business Directory
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Connect</h4>
              <div className="flex space-x-3 mb-6">
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                >
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                >
                  <i className="ri-instagram-fill text-xl"></i>
                </a>
                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                >
                  <i className="ri-twitter-x-fill text-xl"></i>
                </a>
              </div>
              <p className="text-white/80 text-sm">
                <i className="ri-phone-line mr-2"></i>(916) 238-8515
              </p>
              <p className="text-white/80 text-sm mt-2">
                <i className="ri-mail-line mr-2"></i>info@roseville.ca.us
              </p>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-white/60 text-sm">© 2025 City of Roseville, California. All rights reserved.</p>
              <div className="flex items-center space-x-6">
                <a href="#" className="text-white/60 hover:text-white text-sm transition-colors cursor-pointer">
                  Privacy Policy
                </a>
                <a href="#" className="text-white/60 hover:text-white text-sm transition-colors cursor-pointer">
                  Terms of Service
                </a>
                <a
                  href="https://readdy.ai/?ref=logo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-white text-sm transition-colors cursor-pointer"
                >
                  Powered by Readdy
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors cursor-pointer"
      >
        <span className="font-semibold text-gray-900 pr-4">{question}</span>
        <i
          className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line text-gray-400 text-xl flex-shrink-0`}
        ></i>
      </button>
      {isOpen && (
        <div className="px-5 pb-5">
          <p className="text-gray-600 text-sm leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}
